import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Cake as CakeIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { Person, PersonSummary } from '../../types';
import { tokens } from '../../theme';

interface PersonCardProps {
  person: Person | PersonSummary;
  variant?: 'full' | 'summary';
  showActions?: boolean;
  onView?: (person: Person | PersonSummary) => void;
  onEdit?: (person: Person | PersonSummary) => void;
  onDelete?: (person: Person | PersonSummary) => void;
  loading?: boolean;
  selected?: boolean;
}

export const PersonCard: React.FC<PersonCardProps> = ({
  person,
  variant = 'full',
  showActions = true,
  onView,
  onEdit,
  onDelete,
  loading = false,
  selected = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: () => void) => {
    handleMenuClose();
    action();
  };

  const getInitials = (firstName: string, lastName: string): string => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getLifeSpan = (): string => {
    const birthYear = person.birthDate ? new Date(person.birthDate).getFullYear() : null;
    const deathYear = person.deathDate ? new Date(person.deathDate).getFullYear() : null;
    
    if (birthYear && deathYear) {
      return `${birthYear} - ${deathYear}`;
    } else if (birthYear) {
      return `b. ${birthYear}`;
    } else if (deathYear) {
      return `d. ${deathYear}`;
    }
    return '';
  };

  const getAge = (): number | null => {
    if (!person.birthDate) return null;
    
    const birthDate = new Date(person.birthDate);
    const endDate = person.deathDate ? new Date(person.deathDate) : new Date();
    
    return endDate.getFullYear() - birthDate.getFullYear();
  };

  if (loading) {
    return (
      <Card
        sx={{
          height: variant === 'summary' ? 200 : 280,
          transition: tokens.transitions.normal,
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="circular" width={48} height={48} />
            <Box sx={{ ml: 2, flex: 1 }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="text" width="40%" height={20} />
            </Box>
          </Box>
          <Skeleton variant="text" width="80%" height={20} />
          <Skeleton variant="text" width="70%" height={20} />
          <Skeleton variant="text" width="60%" height={20} />
        </CardContent>
      </Card>
    );
  }

  const fullName = `${person.firstName} ${person.lastName}`;
  const displayName = person.maidenName 
    ? `${person.firstName} ${person.lastName} (${person.maidenName})`
    : fullName;

  return (
    <Card
      sx={{
        height: variant === 'summary' ? 200 : 'auto',
        minHeight: variant === 'summary' ? 200 : 280,
        cursor: onView ? 'pointer' : 'default',
        transition: tokens.transitions.normal,
        border: selected ? 2 : 1,
        borderColor: selected ? 'primary.main' : 'divider',
        '&:hover': {
          transform: onView ? 'translateY(-2px)' : 'none',
          boxShadow: tokens.shadows.md,
        },
      }}
      onClick={onView ? () => onView(person) : undefined}
    >
      <CardContent sx={{ pb: variant === 'summary' ? 1 : 2 }}>
        {/* Header with Avatar and Name */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar
            src={'primaryPhotoUrl' in person ? person.primaryPhotoUrl : undefined}
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'primary.main',
              fontSize: '1.1rem',
              fontWeight: 600,
            }}
          >
            {!('primaryPhotoUrl' in person && person.primaryPhotoUrl) && getInitials(person.firstName, person.lastName)}
          </Avatar>
          
          <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontWeight: 600,
                lineHeight: 1.3,
                mb: 0.5,
                wordBreak: 'break-word',
              }}
            >
              {displayName}
            </Typography>
            
            {getLifeSpan() && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <CakeIcon sx={{ fontSize: 16 }} />
                {getLifeSpan()}
                {getAge() && ` (${getAge()} years)`}
              </Typography>
            )}
          </Box>

          {showActions && (
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{ ml: 1 }}
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Box>

        {/* Status Chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: variant === 'summary' ? 1 : 2 }}>
          <Chip
            size="small"
            label={person.gender}
            color="primary"
            variant="outlined"
          />
          
          {person.isDeceased && (
            <Chip
              size="small"
              label="Deceased"
              color="secondary"
              variant="outlined"
            />
          )}
          
          {'isPrivate' in person && person.isPrivate && (
            <Chip
              size="small"
              label="Private"
              color="warning"
              variant="outlined"
            />
          )}
        </Box>

        {/* Details (only in full variant) */}
        {variant === 'full' && (
          <Box sx={{ space: 1 }}>
            {/* Birth Place */}
            {'birthPlace' in person && person.birthPlace && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Born in {person.birthPlace.city}
                  {person.birthPlace.state && `, ${person.birthPlace.state}`}
                  {person.birthPlace.country && `, ${person.birthPlace.country}`}
                </Typography>
              </Box>
            )}

            {/* Occupation */}
            {'occupation' in person && person.occupation && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WorkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {person.occupation}
                </Typography>
              </Box>
            )}

            {/* Biography Preview */}
            {'biography' in person && person.biography && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.4,
                  mt: 1,
                }}
              >
                {person.biography}
              </Typography>
            )}

            {/* Completeness */}
            {'completeness' in person && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Profile {person.completeness}% complete
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    height: 4,
                    bgcolor: 'grey.200',
                    borderRadius: 2,
                    mt: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: `${person.completeness}%`,
                      height: '100%',
                      bgcolor: person.completeness > 80 ? 'success.main' : 
                               person.completeness > 50 ? 'warning.main' : 'error.main',
                      borderRadius: 2,
                      transition: tokens.transitions.normal,
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </CardContent>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {onView && (
          <MenuItem onClick={() => handleAction(() => onView(person))}>
            <ViewIcon sx={{ mr: 1, fontSize: 20 }} />
            View Details
          </MenuItem>
        )}
        {onEdit && (
          <MenuItem onClick={() => handleAction(() => onEdit(person))}>
            <EditIcon sx={{ mr: 1, fontSize: 20 }} />
            Edit
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem 
            onClick={() => handleAction(() => onDelete(person))}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};

// Loading skeleton component
export const PersonCardSkeleton: React.FC<{ variant?: 'full' | 'summary' }> = ({ 
  variant = 'full' 
}) => (
  <PersonCard
    person={{
      id: '',
      firstName: '',
      lastName: '',
      gender: 'male',
      isDeceased: false,
    }}
    variant={variant}
    loading={true}
  />
);

export default PersonCard;